import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ColorRing } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';

import { timeLineChart, brushChartOptions } from './chartOptions';

import useGetTrapDetailTimeLineChartData from 'hooks/Traps/Trap/useGetTrapDetailTimeLineChartData';
import { MCTTimeLineChart } from 'api/traps/types';
import { TrapType } from 'components/AnalyticsPage/TrapType';

interface ChartCardProps {
  slug: string;
  type: TrapType;
  isBarChart?: boolean;
}

interface ChartData {
  date_time: string[];
  caught_pests: number[];
  minMaxSelection: number[];
  caught_empty: number[];
  enter: string[];
}

const TimeLineChartCard: React.FC<ChartCardProps> = ({ slug, type, isBarChart = false }) => {
  const { t } = useTranslation();
  const { data, refetch, isLoading } = useGetTrapDetailTimeLineChartData(slug, type);

  const [chartData, setChartData] = useState<ChartData>({
    date_time: [],
    caught_pests: [],
    minMaxSelection: [],
    caught_empty: [],
    enter: [],
  });

  const chartOptions = timeLineChart(chartData, t, isBarChart, type === TrapType.SRST);

  const brushOptions = brushChartOptions(chartData);

  useEffect(() => {
    refetch();
  }, [slug, refetch]);

  useEffect(() => {
    if (data) {
      const timelineData = data as MCTTimeLineChart;
      setChartData({
        date_time: timelineData.date_time,
        caught_pests: timelineData.caught_pests,
        minMaxSelection: timelineData.minMaxSelection.map(date => new Date(date).getTime()),
        caught_empty: timelineData.caught_empty,
        enter: timelineData.enter,
      });
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="card d-flex m-0">
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: '450px' }}>
          <ColorRing
            visible
            height="80"
            width="80"
            ariaLabel="color-ring-loading"
            colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="card d-flex m-0">
      <div className="card-body">
        <ReactApexChart
          key={isBarChart ? 'bar-view' : 'line-view'}
          options={chartOptions}
          series={chartOptions.series}
          type={isBarChart ? 'bar' : 'area'}
          height={isBarChart ? 470 : 370}
        />

        {!isBarChart && (
          <ReactApexChart
            key="brush-view"
            options={brushOptions}
            series={brushOptions.series}
            type="line"
            height={100}
          />
        )}
      </div>
    </div>
  );
};

export default TimeLineChartCard;
